import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import React from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import MenuItemContent from "src/components/core/MenuItemContent";
import RouteItem, { SubRouteItem } from "src/model/RouteItem.model";
import { setDepositModal, setWithdrawModal } from "src/store/features/modals.slice";
import { useSettingsState } from "src/store/features/settings.slice";
import { useAppDispatch } from "src/store/store";
import usePermission from "src/utils/hooks/usePermission";
import "./styles/navlink.css";
import { Link as RouterLink } from "react-router-dom";

const StyledAccordion = styled(Accordion)({
  background: "transparent",
  boxShadow: "none",

  "&:before": {
    display: "none",
  },
  "& .Mui-expanded": {
    backgroundColor: "#012d4c",
  },
});

const StyledAccordionSummary = styled(AccordionSummary)({
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
});

const MenuLink = styled(RouterLink)({
  textDecoration: "none",
  color: "#FFF",
  width: "100%",
});

const MenuItemWrapper = styled("div")({
  textDecoration: "none",
  color: "#FFF",
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const SubRoutesList = styled("ul")({
  paddingLeft: "24px",
});

const SubRouteMenu = styled("li")({
  borderLeft: "3px solid #F8F9F940",
  padding: "8px",
  paddingLeft: "20px",
});

const SubRouteLink = styled(RouterLink)({
  textDecoration: "none",
  color: "#FFF",
});

const SubRouteContent = styled("div")({});

interface MenuItemProps {
  route: RouteItem;
  handleMenuClose: () => void;
}

// functional component
const MenuItem = ({ route, handleMenuClose }: MenuItemProps) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const { expandedMenuItem } = useSettingsState();
  const { userPermission } = usePermission();
  const isGuest = userPermission === "guest";

  /**
   * Determines whether the Accordion should be expanded based on sub-routes, expanded menu items, and the current path.
   *
   * @param {SubRouteItem[]} subRoutes - An array of sub-routes for the current route.
   * @param {string[]} expandedMenuItem - An array of routes that should trigger the Accordion to expand.
   * @param {string} currentPath - The current location's path (e.g., window.location.pathname).
   *
   * @returns {boolean} - Returns true if the Accordion should be expanded, otherwise false.
   */
  const shouldExpand = (
    subRoutes: SubRouteItem[] = [],
    expandedMenuItem: string[],
    currentPath: string,
  ): boolean => {
    return subRoutes?.some(
      (subRoute) => expandedMenuItem?.includes(subRoute.path) || subRoute.path === currentPath,
    );
  };

  const [expanded, setExpanded] = React.useState<boolean>(
    shouldExpand(route.subRoutes, expandedMenuItem, location.pathname),
  );

  const handleClick = (path: string) => {
    if (path === "/wallets/deposit") {
      dispatch(setDepositModal(true));
    }
    if (path === "/wallets/withdraw") {
      dispatch(setWithdrawModal(true));
    }
  };

  return (
    <StyledAccordion
      expanded={expanded && !!route.subRoutes}
      onChange={(e, isExpanded) => {
        handleClick(route.path);
        setExpanded(isExpanded);
      }}
      disableGutters={true}>
      <StyledAccordionSummary
        id={`panel-${route.title}-header`}
        expandIcon={route.subRoutes && <GridExpandMoreIcon sx={{ color: "#FFF" }} />}>
        {!route.subRoutes ? (
          <MenuLink
            to={route.path}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              setExpanded((prev) => !prev);
              handleMenuClose();
              trackEvent(`Select_Navigation`, { route: route.title });
            }}>
            <MenuItemContent route={route} expanded={expanded} />
          </MenuLink>
        ) : (
          <MenuItemWrapper
            onClick={() => {
              setExpanded(!expanded);
              trackEvent(`Select_Navigation`, { route: route.title });
            }}>
            <MenuItemContent route={route} expanded={expanded} />
          </MenuItemWrapper>
        )}
      </StyledAccordionSummary>

      <AccordionDetails sx={{ padding: 0 }}>
        <SubRoutesList>
          {route.subRoutes
            ?.filter((subRoute) => {
              if (subRoute.path === "/addresses") {
                return !isGuest;
              }
              return true;
            })
            .map((subRoute, i) => (
              <SubRouteMenu key={i + subRoute.path}>
                <SubRouteLink
                  to={`${subRoute.path}`}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    handleClick(subRoute.path);
                    trackEvent(`Select_Navigation`, { route });
                    if (isMobile) handleMenuClose();
                  }}>
                  <SubRouteContent>
                    <MenuItemContent route={subRoute} />
                  </SubRouteContent>
                </SubRouteLink>
              </SubRouteMenu>
            ))}
        </SubRoutesList>
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default MenuItem;
